<template>
  <div>
    <div v-if="loaded">
      <!-- Row top buttons -->
      <div class="row q-mb-lg">
        <div class="col-6" style="justify-content: flex-start">
          <q-btn
            class="capitalize ctrl-button"
            color="grey-5"
            :label="`${$store.state.stringsStore.strings.action.toOverview}`"
            @click="$router.push('/popup')"
            icon="keyboard_backspace"
          />
        </div>
        <div class="col-6">
          <q-btn  style="float: right"
            class="capitalize ctrl-button right q-ml-md"
            :color="$v.data.$anyError ? 'grey-5' : 'ctrl-turquoise'"
            :disable="$v.data.$anyError"
            :label="`${$store.state.stringsStore.strings.action.save}`"
            @click="save()"
            icon-right="save"
          />
          <q-btn v-if="$route.params.id"
            class="q-ml-md capitalize ctrl-button right"
            icon-right="delete"
            color="negative capitalize"
            :label="$store.state.stringsStore.strings.action.delete"
            align="right"
            @click="deleteTag()"
          />
        </div>
      </div>

      <!-- Columns -->
      <div class="row gutter-sm q-mb-lg">
        <!-- Left -->
        <div class="col-md-6 col-sm-12 col-xs-12 q-pa-sm">
          <div class="row q-mb-sm">
            <div class="full-width">
              <p class="card-title">
                {{ getString('action.details') }}
              </p>

              <div class="card-block">
                <div class="row">
                  <q-input
                    type="text"
                    class="full-width q-mb-md"
                    :float-label="getString('title')"
                    :error="labelSet && !$v.data.title.required"
                    v-model="data.title"
                  />
                </div>
                <div class="row">
                  <div class="col-xs-12 col-md-6">
                    <q-datetime
                      format24h
                      class="full-width q-mb-md"
                      :float-label="'Datetime start'"
                      v-model="data.datetime_start"
                      type="datetime" />
                  </div>

                  <div class="col-xs-12 col-md-6">
                    <q-datetime
                      format24h
                      class="full-width q-mb-md"
                      :float-label="'Datetime end'"
                      v-model="data.datetime_end"
                      type="datetime" />
                  </div>
                </div>

                <div class="row q-mb-sm q-mt-md">
                  <q-toggle
                    class="q-mr-lg"
                    color="secondary"
                    :label="'Popup'"
                    v-model="data.popup"/>
                  <q-toggle
                    color="secondary"
                    class="q-mr-lg"
                    :label="getString('module.popup.show_once')"
                    v-model="data.show_once"/>
                  <q-toggle
                    color="secondary"
                    :label="getString('module.popup.login')"
                    v-model="data.login"/>
                </div>
              </div>

            </div>
          </div>
        </div>

      </div>

      <!-- Right -->
      <div class="row q-mb-sm">
        <div class="full-width">
          <p class="card-title">
            {{ getString('module.popup.content') }}
          </p>

          <div class="card-block">
            <div class="row">
              <editor
                tinymce-script-src="https://cdnjs.cloudflare.com/ajax/libs/tinymce/4.9.11/tinymce.min.js"
                :init="tinyConfig()"
                ref="tm"
                v-model="data.content">
              </editor>
            </div>
          </div>

        </div>
      </div>
      <!-- Image select modal -->
      <q-modal no-esc-dismiss v-if="imageModal" v-model="imageModal">
        <div class="row" style="display: flex; justify-content: flex-end">
          <q-btn
            icon="close"
            @click="imageModal = false"
            />
        </div>
        <div style="height: 75vh; width: 80vw;">
          <div class="row q-pa-md">
            <div class="col-7 q-pa-md">
              <library-table :guid="guid" :readonly="true" spacing="45vh"/>
            </div>
            <div class="col-5 q-pa-md">
              <div>
                <image-preview :guid="guid"/>
              </div>
            </div>
          </div>
        </div>
      </q-modal>

    </div>
    <div class="loader" v-else>
      <q-spinner color="grey-5" :size="80" />
    </div>
  </div>
</template>

<script>
import EventBus from '@/event-bus';
import { required } from 'vuelidate/lib/validators';
import moment from 'moment';
import qs from 'qs';

// Mixins
import fetchDetail from '@/mixins/detailViews/fetchDetail';
import createUuid from '@/helpers/createUuid';
import getString from '@/helpers/GetStrings';

// Components
import LibraryTable from '@/components/library/LibraryTable.vue';
import ImagePreview from '@/components/library/ImagePreview.vue';
import common from '../common';

const Editor = require('@tinymce/tinymce-vue').default;

export default {
  mixins: [
    common,
    fetchDetail,
  ],
  components: {
    LibraryTable,
    ImagePreview,
    Editor,
  },
  validations: {
    data: {
      label: {
        required,
      },
    },
  },
  data() {
    return {
      loaded: false,
      guid: createUuid(),
      imageModal: false,
      containsErrors: true,
      data: {
        title: '',
        content: '',
        datetime_start: new Date(),
        datetime_end: null,
        popup: true,
        show_once: false,
      },
      labelSet: false,
      plugins: [],
    };
  },

  methods: {
    getString,
    insertImage(editor) {
      // Create a unique Id to listen to incoming events for this instance of the image selector.
      const imageGuid = createUuid();

      // Update guid. (Prop passed to image preview.)
      this.guid = imageGuid;

      EventBus.$on(`image-select:${imageGuid}`, (data) => {
        // Image selected, remove the event listener, close the modal.
        EventBus.$off(`image-select:${imageGuid}`);
        this.imageModal = false;

        const range = editor.selection.getRng();
        const newNode = editor.getDoc().createElement('img'); // create img node in the editor
        newNode.src = data.src; // add src attribute

        if (data.meta.alt) {
          newNode.alt = data.meta.alt; // Conditionally include the meta tag for the image (read from library)
        }

        range.insertNode(newNode);

        // Broadcast the change
        this.broadcast();
      });
      this.imageModal = true;
    },

    tinyConfig() {
      return {
        branding: false,
        browser_spellcheck: true,
        plugins: this.plugins,
        toolbar: [
          'undo redo | styleselect | bold italic | link image alignleft aligncenter alignright | imageSelect | code',
        ],
        templates: [
          { title: 'Name', description: 'Users full name', content: `${this.$store.state.meStore.me.name.firstname} ${this.$store.state.meStore.me.name.surname}` },
        ],

        setup: (editor) => {
          editor.addButton('imageSelect', {
            tooltip: 'Select an image from the library',
            icon: 'books',
            onclick: () => {
              this.insertImage(editor);
            },
          });
        },
        pagebreak_split_block: true,
      };
    },

    validateLabel() {
      this.labelSet = true;
      this.$v.$touch();
    },
    deleteTag() {
      // Dialog
      this.$q.dialog({
        title: '',
        message: getString('upload.delete.text').replace('%file%', this.$store.state.stringsStore.language === 'en' ? 'popup' : 'deze popup'),
        ok: this.$store.state.stringsStore.strings.dialog.confirm.ok,
        cancel: this.$store.state.stringsStore.strings.dialog.confirm.cancel,
      }).then(() => {
        // delete call
        this.axios.delete(`${this.endpoint}/${this.data.id}`)
          .then(() => {
            // Refresh data to see the result
            this.$q.notify({
              message: getString('module.popup.edit.delete.single'),
              position: 'top-right',
              timeout: 1000,
              color: 'positive',
            });
            this.$router.push('/popup');
          })
          .catch(() => {
            // Error
          });
      }).catch(() => {
      });
    },

    save() {
      const params = {
        title: this.data.title,
        content: this.data.content,
        popup: this.data.popup ? 1 : 0,
        show_once: this.data.show_once ? 1 : 0,
        login: this.data.login ? 1 : 0,
        datetime_start: moment(this.data.datetime_start).utc().format('YYYY-MM-DD HH:mm:ss'),
        datetime_end: this.data.datetime_end ? moment(this.data.datetime_end).utc().format('YYYY-MM-DD HH:mm:ss') : null,
      };

      for (const [key, value] of Object.entries(params)) {
        // If the value is not null, undefined or empty, remove it from the params.
        if (!value) delete params[key];
      }

      // Check if adding or modifying
      if (!this.data.id) {
        // Adding a new popup... First post label and url
        this.axios.post('popup', qs.stringify(params)).then(async (res) => {
          this.$q.notify({
            message: this.getString('module.popup.edit.save.success'),
            timeout: 2000,
            type: 'positive',
            position: 'top-right',
          });
          if (res.headers.location) {
            const id = res.headers.location.split('/').pop();
            if (id) {
              this.$router.push(`/popup/${id}`);
            }
          }
        });
      } else {
        // The popup already exists, update with new values
        this.axios.put(`popup/${this.data.id}`, qs.stringify(params)).then(() => {
          this.$q.notify({
            message: this.getString('module.popup.edit.save.success'),
            timeout: 2000,
            type: 'positive',
            position: 'top-right',
          });
        });
      }
    },
  },

  async mounted() {
    if (this.$route.params.id) {
      this.axios.get(`popup/${this.$route.params.id}`).then((res) => {
        this.loaded = true;
        const popup = res.data.popup;
        this.data.title = popup.title;
        this.data.content = popup.content;
        this.data.datetime_start = new Date(popup.datetime_start);
        this.data.datetime_end = popup.datetime_end ? new Date(popup.datetime_end) : null;
        this.data.popup = popup.popup;
        this.data.show_once = popup.show_once;
        this.data.login = popup.login;
      });
    } else {
      this.loaded = true;
    }
  },
};
</script>

<style lang="stylus" scoped>
.full-height
  height: 100%
</style>
